import {useNavigate, useParams} from 'react-router-dom';
import LoadingContent from '../../../components/LoadingContent';
import {useMediaQuery, useTheme, IconButton, Box} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {useSnackbar} from 'notistack';
import useHandleErrors from '../../../hooks/useHandleErrors';
import server from '../../../global/server';
import '@livekit/components-styles';
import {
    ConnectionStateToast,
    ControlBar,
    GridLayout,
    LiveKitRoom,
    ParticipantTile,
    RoomAudioRenderer,
    useTracks,
    useRemoteParticipants,
    useLocalParticipant
} from '@livekit/components-react';
import {Track} from 'livekit-client';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';
  
export default () => {
    const params = useParams();
    const [errs, setErrs] = useState();
    useHandleErrors(errs);

    const [token, setToken] = useState();
    const [room, setRoom] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [rules, setRules] = useState();
    const [observers, setObservers] = useState([]);

    const navigation = useNavigate();
    const {enqueueSnackbar} = useSnackbar();

    // Quando a saida parte do proprio usuario, o componente desmonta antes do
    // LiveKit avisar a desconexao: sem essa marca, ele seria levado para os
    // detalhes do encontro depois de ja ter navegado para outro lugar
    const mounted = useRef(true);
    useEffect(() => () => {
        mounted.current = false;
    }, []);

    // O anfitrião finalizar o encontro derruba a sala no servidor, e e assim
    // que o conviva descobre que acabou
    const handleDisconnected = async () => {
        if (!mounted.current) return;

        try {
            const {data} = await server.get(`/booking/${params.id}`);
            if (!mounted.current) return;

            if (data.data.finished) {
                enqueueSnackbar('O encontro foi finalizado.', {variant: 'info'});
            } else {
                enqueueSnackbar('Você foi desconectado da chamada.', {variant: 'warning'});
            }
        } catch (error) {
            console.log(error);
            if (!mounted.current) return;
            enqueueSnackbar('Você foi desconectado da chamada.', {variant: 'warning'});
        }

        navigation(`/encontros/${params.id}/detalhes`);
    };

    const getToken = async () => {
        try {
            const {data} = await server.get(`/booking/${params.id}/call`, {
                params: {
                    screenX: window.screen.width,
                    screenY: window.screen.height,
                },
            });
            setToken(data.data.token);
            setRules(data.data.rules);
        } catch (error) {
            console.log(error);
            setErrs(error);
        }
    };

    useEffect(() => {
        getToken();
    }, []);

    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Detectar mobile para definir câmera frontal por padrão
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );

    if (!Boolean(token)) return <LoadingContent />;

    return (
        <LiveKitRoom
            video={isMobile ? { facingMode: 'user' } : true}
            audio={true}
            token={token}
            connectOptions={{autoSubscribe: true}}
            onDisconnected={handleDisconnected}
            serverUrl="wss://empiria-nwz9jcl0.livekit.cloud"
            data-lk-theme="default">
            <VideoConference  />
            <ControlBar
                controls={{leave: false, screenShare: false, chat: false}}
            />
            <RoomAudioRenderer />
            <ConnectionStateToast />
        </LiveKitRoom>
    );
};

function VideoConference() {
    const tracks = useTracks([
        {source: Track.Source.Camera, withPlaceholder: true},
        {source: Track.Source.ScreenShare, withPlaceholder: false}
    ], {
        onlySubscribed: true,
    });

    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const {localParticipant} = useLocalParticipant();
    const [currentFacingMode, setCurrentFacingMode] = useState('user');

    // Detectar se é dispositivo móvel (celular/tablet)
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
    );

    const switchCamera = async () => {
        if (!isMobileDevice) return;

        try {
            const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';

            // Usar a API do LiveKit para trocar câmera
            const cameraTrack = localParticipant?.getTrackPublication(Track.Source.Camera);

            if (cameraTrack?.track) {
                // Parar o track atual
                await cameraTrack.track.stop();
                await localParticipant?.unpublishTrack(cameraTrack.track);
            }

            // Criar novo track com a câmera oposta
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: newFacingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            });

            const newVideoTrack = newStream.getVideoTracks()[0];

            // Publicar o novo track
            await localParticipant?.publishTrack(newVideoTrack, {
                source: Track.Source.Camera,
            });

            setCurrentFacingMode(newFacingMode);
        } catch (error) {
            console.error('Erro ao trocar câmera:', error);
            // Reativar câmera se falhar
            await localParticipant?.setCameraEnabled(true);
        }
    };

    // No celular a barra de endereco entra no calculo do 100vh e empurra a
    // barra de controles (microfone e camera) para fora da tela
    return (
        <Box sx={{
            position: 'relative',
            height: 'calc(100vh - 200px)',
            '@supports (height: 100dvh)': { height: 'calc(100dvh - 200px)' },
        }}>
            <GridLayout
                tracks={tracks}
                style={{ height: '100%' }}
            >
                <ParticipantTile />
            </GridLayout>

            {isMobileDevice && (
                <IconButton
                    onClick={switchCamera}
                    sx={{
                        position: 'absolute',
                        bottom: 80,
                        right: 16,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        },
                        zIndex: 1000,
                        width: 56,
                        height: 56,
                    }}
                    size="large"
                >
                    <FlipCameraIosIcon sx={{ fontSize: 28 }} />
                </IconButton>
            )}
        </Box>
    );
}